<template>
  <div class="details-wrapper">
    <div class="schedule-timeline">
      <div
        v-for="timeSlot in timeSlots"
        :key="timeSlot.id"
        class="time-slot"
        :class="{
          'time-slot--price': timeSlot.type === 'price',
          'time-slot--time': timeSlot.type === 'time'
        }"
      >
        <component
          v-if="timeSlot.icon"
          :is="timeSlot.icon"
          class="time-icon"
          @mousedown.stop
        />
        <span class="time-label">{{ timeSlot.label }}</span>
        <span
          v-if="timeSlot.type === 'time'"
          class="dosage-total"
        >
          {{ totals[timeSlot.key] > 0 ? totals[timeSlot.key] : '' }}
        </span>
      </div>
    </div>
    <div class="supplement-schedule-list">
      <div
        v-for="supplement in supplements"
        :key="supplement.id"
        class="supplement-row"
        :class="{
          'is-hovered': hoveredSupplementId === supplement.id,
          'is-expanded': expandedRows.includes(supplement.id),
          'is-locked': isVisitLocked,
          'is-highlighted': highlightedSupplementId === supplement.supplementId
        }"
        @mouseenter="handleMouseEnter(supplement)"
        @mouseleave="handleMouseLeave"
      >
        <div class="supplement-info">
          <div class="supplement-header"
               :class="{'disabled': visit.status === 'Completed'}"
               @click="toggleExpand(supplement.id)"
          >
            <div class="supplement-actions"
                 v-if="!isVisitLocked"
                 @click.stop
            >
              <actions
                :items="actions"
                :item="supplement"
                :useSvgIcons="true"
                custom-class="actions--results"
                v-if="!visit.finishedOn || !['InProtocol', 'NeedProduct', 'Completed', 'ProtocolEnding'].includes(visit.status)"
                @action="runAction"
              />
            </div>
            <div class="supplement-name">
               <UserCardIcon
                 v-if="supplement.sourceName === 'User-Added'"
                 @mousedown.stop
               />
              <span
                :class="{'disabled': visit.status === 'Completed'}"
                @click.stop="editResult(supplement)"
              >
                {{ supplement.supplementName }}
              </span>
            </div>
            <supplement-icons
              :supplement="supplement"
              @show-crg-notes="$emit('show-crg-notes', supplement)"
              @warning-notes="$emit('warning-notes', supplement)"
            />
            <div class="total-price">
              ${{supplement.totalCost ? supplement.totalCost.toFixed(2) : ''}}
            </div>
          </div>
          <div class="product-dosage">
            <div class="product-dosage__inner">
              <div class="dosage-cell">
                <v-menu
                  :close-on-content-click="true"
                  offset-x
                  :disabled="isVisitLocked"
                  max-width="50px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dosage-cell-content"
                         :class="{ active: isActiveCell(supplement, 'dosageUponArising') }"
                         v-bind="attrs"
                         v-on="on"
                         @click="setActiveCell(supplement, 'dosageUponArising')"
                    >
                      <span>{{supplement.dosageUponArising ? supplement.dosageUponArising : ''}}</span>
                    </div>
                  </template>
                  <dosage-control
                    :dosages="supplementDosages"
                    :itemRow="supplement"
                    :propertyKey="'dosageUponArising'"
                    @update="updateSupplementDosage"
                  />
                </v-menu>
              </div>
              <div class="dosage-cell">
                <v-menu
                  :close-on-content-click="true"
                  offset-x
                  :disabled="isVisitLocked"
                  max-width="50px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dosage-cell-content"
                         :class="{ active: isActiveCell(supplement, 'dosageBreakfast') }"
                         v-bind="attrs"
                         v-on="on"
                         @click="setActiveCell(supplement, 'dosageBreakfast')"
                    >
                      <span>{{supplement.dosageBreakfast ? supplement.dosageBreakfast : ''}}</span>
                    </div>
                  </template>
                  <dosage-control
                    :dosages="supplementDosages"
                    :itemRow="supplement"
                    :propertyKey="'dosageBreakfast'"
                    @update="updateSupplementDosage"
                  />
                </v-menu>
              </div>
              <div class="dosage-cell">
                <v-menu
                  :close-on-content-click="true"
                  offset-x
                  :disabled="isVisitLocked"
                  max-width="50px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dosage-cell-content"
                         :class="{ active: isActiveCell(supplement, 'dosage10am') }"
                         v-bind="attrs"
                         v-on="on"
                         @click="setActiveCell(supplement, 'dosage10am')"
                    >
                      <span>{{supplement.dosage10am ? supplement.dosage10am : ''}}</span>
                    </div>
                  </template>
                  <dosage-control
                    :dosages="supplementDosages"
                    :itemRow="supplement"
                    :propertyKey="'dosage10am'"
                    @update="updateSupplementDosage"
                  />
                </v-menu>
              </div>
              <div class="dosage-cell">
                <v-menu
                  :close-on-content-click="true"
                  offset-x
                  :disabled="isVisitLocked"
                  max-width="50px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dosage-cell-content"
                         :class="{ active: isActiveCell(supplement, 'dosageLunch') }"
                         v-bind="attrs"
                         v-on="on"
                         @click="setActiveCell(supplement, 'dosageLunch')"
                    >
                      <span>{{supplement.dosageLunch ? supplement.dosageLunch : ''}}</span>
                    </div>
                  </template>
                  <dosage-control
                    :dosages="supplementDosages"
                    :itemRow="supplement"
                    :propertyKey="'dosageLunch'"
                    @update="updateSupplementDosage"
                  />
                </v-menu>
              </div>
              <div class="dosage-cell">
                <v-menu
                  :close-on-content-click="true"
                  offset-x
                  :disabled="isVisitLocked"
                  max-width="50px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dosage-cell-content"
                         :class="{ active: isActiveCell(supplement, 'dosage3pm') }"
                         v-bind="attrs"
                         v-on="on"
                         @click="setActiveCell(supplement, 'dosage3pm')"
                    >
                      <span>{{supplement.dosage3pm ? supplement.dosage3pm : ''}}</span>
                    </div>
                  </template>
                  <dosage-control
                    :dosages="supplementDosages"
                    :itemRow="supplement"
                    :propertyKey="'dosage3pm'"
                    @update="updateSupplementDosage"
                  />
                </v-menu>
              </div>
              <div class="dosage-cell">
                <v-menu
                  :close-on-content-click="true"
                  offset-x
                  :disabled="isVisitLocked"
                  max-width="50px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dosage-cell-content"
                         :class="{ active: isActiveCell(supplement, 'dosageDinner') }"
                         v-bind="attrs"
                         v-on="on"
                         @click="setActiveCell(supplement, 'dosageDinner')"
                    >
                      <span>{{supplement.dosageDinner ? supplement.dosageDinner : ''}}</span>
                    </div>
                  </template>
                  <dosage-control
                    :dosages="supplementDosages"
                    :itemRow="supplement"
                    :propertyKey="'dosageDinner'"
                    @update="updateSupplementDosage"
                  />
                </v-menu>
              </div>
              <div class="dosage-cell">
                <v-menu
                  :close-on-content-click="true"
                  offset-x
                  :disabled="isVisitLocked"
                  max-width="50px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dosage-cell-content"
                         :class="{ active: isActiveCell(supplement, 'dosage7pm') }"
                         v-bind="attrs"
                         v-on="on"
                         @click="setActiveCell(supplement, 'dosage7pm')"
                    >
                      <span>{{supplement.dosage7pm ? supplement.dosage7pm : ''}}</span>
                    </div>
                  </template>
                  <dosage-control
                    :dosages="supplementDosages"
                    :itemRow="supplement"
                    :propertyKey="'dosage7pm'"
                    @update="updateSupplementDosage"
                  />
                </v-menu>
              </div>
              <div class="dosage-cell">
                <v-menu
                  :close-on-content-click="true"
                  offset-x
                  :disabled="isVisitLocked"
                  max-width="50px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dosage-cell-content"
                         :class="{ active: isActiveCell(supplement, 'dosageBeforeSleep') }"
                         v-bind="attrs"
                         v-on="on"
                         @click="setActiveCell(supplement, 'dosageBeforeSleep')"
                    >
                      <span>{{supplement.dosageBeforeSleep ? supplement.dosageBeforeSleep : ''}}</span>
                    </div>
                  </template>
                  <dosage-control
                    :dosages="supplementDosages"
                    :itemRow="supplement"
                    :propertyKey="'dosageBeforeSleep'"
                    @update="updateSupplementDosage"
                  />
                </v-menu>
              </div>

              <div class="dosage-cell">
                <div class="dosage-cell-content">
                  <span>{{supplement.daysSupply ? supplement.daysSupply : ''}}</span>
                </div>
              </div>
            </div>
            <div class="products-details" @click="toggleExpand(supplement.id)">
              <div class="product-summary">
                <div class="product-icon">
                  <BottlesIcon
                    v-if="(supplement.resultProducts?.$values || []).length > 1"
                    @mousedown.stop
                  />
                  <BottleIcon
                    v-else
                    @mousedown.stop
                  />
                  <span class="product-count">{{ supplement.totalBottles }}</span>
                </div>
                <div class="product-info">
                  <span class="product-doses">{{ getProductInfo(supplement) }}</span>
                </div>
              </div>
              <ArrowGreenIcon class="expand-icon" :class="{ expanded: expandedRows.includes(supplement.id) }" @mousedown.stop />
            </div>
          </div>

          <div v-if="expandedRows.includes(supplement.id)" class="supplement-expanded">
            <div class="expanded-grid-header">
              <div class="grid-cell"></div>
              <div class="grid-cell">PRODUCT</div>
              <div class="grid-cell">SIZE</div>
              <div class="grid-cell"># OF BOTTLES</div>
              <div class="grid-cell">UNIT COST</div>
              <div class="grid-cell">TOTAL COST</div>
            </div>

            <div class="expanded-grid-body">
              <div
                v-for="product in supplement.products.$values"
                :key="product.id"
                class="grid-row"
              >
                <div class="grid-cell checkbox-cell">
                  <checkbox
                    :inputValue=product.selected
                    color="#008C89"
                    :disabled="
                      isVisitLocked ||
                      isLastSelectedProduct(supplement, product)
                    "
                    @change="toggleSupplementProduct(product)"
                  ></checkbox>
                </div>
                <div class="grid-cell">
                  {{ product.number }}
                </div>
                <div class="grid-cell">
                  {{ product.doses }} {{ product.units }}
                </div>
                <div class="grid-cell">
                  <div class="bottles-cell">
                    <button
                      class="btn-control"
                      @click="updateQuantityBottles(-1, product)"
                      :disabled="product.quantity <= 1 || !product.selected || isVisitLocked"
                    >
                      <MinusIcon class="btn-control-icon" @mousedown.stop />
                    </button>
                    <span class="bottle-count">{{ product.quantity }}</span>
                    <button
                      class="btn-control"
                      @click="updateQuantityBottles(1, product)"
                      :disabled="!product.selected || isVisitLocked"
                    >
                      <PlusIcon class="btn-control-icon" @mousedown.stop />
                    </button>
                  </div>
                </div>
                <div class="grid-cell">
                  ${{ getUnitPrice(product) }}
                </div>
                <div class="grid-cell">
                  ${{ getTotalPrice(product) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <add-result :orderMethod="orderMethod" v-if="dialogs.editResult.isOpen || dialogs.addResult.isOpen" />
    <confirm v-if="dialogs.confirm.isOpen" @close="updateAnything" />
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import Image from '@/mixins/Image'
import { trackEvent } from '@/utils/analytics'
import actions from '@/components/layouts/actions'
import DosageControl from '@/components/elements/dosage-control'
import checkbox from '@/components/elements/checkbox'
import addResult from '@/components/layouts/dialogs/add-result'
import confirm from '@/components/layouts/dialogs/confirm-dialog'
import SupplementIcons from '@/components/elements/supplement-icons'

import UserCardIcon from '@/assets/icons/components/user-card.svg'
import BottleIcon from '@/assets/icons/components/bottle.svg'
import BottlesIcon from '@/assets/icons/components/bottles.svg'
import MinusIcon from '@/assets/icons/components/icon-minus.svg'
import PlusIcon from '@/assets/icons/components/icon-plus.svg'
import ScheduleIcon1 from '@/assets/icons/components/schedule-icons/schedule-icon_1.svg'
import ScheduleIcon2 from '@/assets/icons/components/schedule-icons/schedule-icon_2.svg'
import ScheduleIcon3 from '@/assets/icons/components/schedule-icons/schedule-icon_3.svg'
import ScheduleIcon4 from '@/assets/icons/components/schedule-icons/schedule-icon_4.svg'
import ScheduleIcon5 from '@/assets/icons/components/schedule-icons/schedule-icon_5.svg'
import ScheduleIcon6 from '@/assets/icons/components/schedule-icons/schedule-icon_6.svg'
import ScheduleIcon7 from '@/assets/icons/components/schedule-icons/schedule-icon_7.svg'
import ScheduleIcon8 from '@/assets/icons/components/schedule-icons/schedule-icon_8.svg'
import ArrowGreenIcon from '@/assets/icons/components/arrow-green.svg'

const TIME_SLOTS = [
  { id: 'upon-arising', label: 'Upon Arising', icon: 'ScheduleIcon1', type: 'time', key: 'totalDosageUponArising' },
  { id: 'breakfast', label: 'Breakfast', icon: 'ScheduleIcon2', type: 'time', key: 'totalDosageBreakfast' },
  { id: '10am', label: '10 am', icon: 'ScheduleIcon3', type: 'time', key: 'totalDosage10am' },
  { id: 'lunch', label: 'Lunch', icon: 'ScheduleIcon4', type: 'time', key: 'totalDosageLunch' },
  { id: '3pm', label: '3 pm', icon: 'ScheduleIcon5', type: 'time', key: 'totalDosage3pm' },
  { id: 'dinner', label: 'Dinner', icon: 'ScheduleIcon6', type: 'time', key: 'totalDosageDinner' },
  { id: '7pm', label: '7 pm', icon: 'ScheduleIcon7', type: 'time', key: 'totalDosage7pm' },
  { id: 'bedtime', label: 'Bedtime', icon: 'ScheduleIcon8', type: 'time', key: 'totalDosageBeforeSleep' },
  { id: 'days-supply', label: 'Days\nsupply', icon: '', type: 'time', key: 'daysSupply' },
  { id: 'price', label: 'Price', icon: '', type: 'price', width: 'wide' },
]

export default {
  name: 'SupplementSchedule',
  components: {
    SupplementIcons,
    confirm,
    addResult,
    checkbox,
    DosageControl,
    actions,
    UserCardIcon,
    BottleIcon,
    BottlesIcon,
    MinusIcon,
    PlusIcon,
    ScheduleIcon1,
    ScheduleIcon2,
    ScheduleIcon3,
    ScheduleIcon4,
    ScheduleIcon5,
    ScheduleIcon6,
    ScheduleIcon7,
    ScheduleIcon8,
    ArrowGreenIcon
  },
  mixins: [Image],
  props: {
    headers: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    },
    orderMethod: {
      type: Object
    },
    highlightedSupplementId: {
      type: [Number, String],
      default: null
    },
    totals: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      timeSlots: TIME_SLOTS,
      supplementDosages: [
        {
          id: 1,
          active: false,
          value: 1,
          text: '1'
        },
        {
          id: 2,
          active: false,
          value: 2,
          text: '2'
        },
        {
          id: 3,
          active: true,
          value: 3,
          text: '3'
        },
        {
          id: 4,
          active: false,
          value: 4,
          text: '4'
        },
        {
          id: 5,
          active: false,
          value: 5,
          text: '5'
        },
        {
          id: 6,
          active: false,
          value: 0,
          text: 'x'
        }
      ],
      expandedRows: [],
      hoveredSupplementId: null,
      activeCell: null,
    }
  },
  computed: {
    ...mapState({
      visit: state => state.visits?.visit
    }),
    isVisitLocked() {
      return ['InProtocol', 'Completed', 'Closed', 'ProtocolEnding']
        .includes(this.visit?.status)
    },
    actions () {
      return [
        { svgIcon: 'icon-plus-2', name: 'Add Notes', action: this.editResult },
        { svgIcon: 'icon-trash', name: 'Delete', action: this.deleteResult }
      ]
    },
    dialogs () {
      return this.$store.state.dialogs
    },
    results () {
      return this.$store.state?.results?.results
    },
    supplements() {
      if (!this.items || this.items.length === 0) {
        return []
      }

      const allSupplements = []

      this.items.forEach(item => {
        if (item.resultSupplements && item.resultSupplements.$values) {
          allSupplements.push(...item.resultSupplements.$values)
        }
      })

      return allSupplements
    },
  },
  methods: {
    ...mapActions('results', ['getResults', 'getNotes', 'updateResult', 'updateSupplementProduct', 'createSupplementProduct', 'removeSupplementProduct']),
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),
    ...mapMutations('results', ['UPDATE_DATA','UPDATE_STORE_DATA']),

    setActiveCell(row, key) {
      this.activeCell = `${row.id}-${key}`
    },
    isActiveCell(row, key) {
      return this.activeCell === `${row.id}-${key}`
    },

    toggleExpand(supplementId) {
      const index = this.expandedRows.indexOf(supplementId)
      if (index > -1) {
        this.expandedRows.splice(index, 1)
      } else {
        // this.expandedRows = []
        // this.expandedRows.push(supplementId)

        this.expandedRows = [supplementId]
      }
    },
    async updateQuantityBottles (val, product) {
      const newQuantity = product.quantity + val

      if (newQuantity >= 1) {
        const response = await this.updateSupplementProduct({
          resultSupplementId: product.resultSupplementId,
          supplementProductId: product.id,
          numberOfBottles: newQuantity
        })

        await this.getResults({
          id: this.$route.params.id,
          orderMethod: this.orderMethod.code
        })

        this.getNotes(this.$route.params.id)
        this.UPDATE_STORE_DATA(response)
      }
    },
    getUnitPrice(product) {
      if (product.selected) {
        return product.price?.toFixed(2) || '0.00'
      } else {
        return product.listPrice?.toFixed(2) || '0.00'
      }
    },
    getTotalPrice(product) {
      return product.total?.toFixed(2) || '0.00'
    },
    isLastSelectedProduct(supplement, product) {
      if (!product.selected) return false

      const selectedCount = supplement.products.$values.filter(p => p.selected).length
      return selectedCount === 1
    },
    async toggleSupplementProduct(product) {
      if (!product.selected) {
        const res = await this.createSupplementProduct({
          resultSupplementId: product.resultSupplementId,
          supplementProductId: product.supplementProductId,
          numberOfBottles: 1,
          price: product.listPrice
        })

        this.UPDATE_STORE_DATA(res)
      } else {
        const res = await this.removeSupplementProduct({
          resultSupplementId: product.resultSupplementId,
          supplementProductId: product.id
        })

        this.UPDATE_STORE_DATA(res)
      }
      await this.getResults({
        id: this.$route.params.id,
        orderMethod: this.orderMethod.code
      })
      this.getNotes(this.$route.params.id)
    },
    async updateSupplementDosage ({ data, keyProp }) {
      const result = await this.updateResult(data)
    },
    editResult (item) {
      trackEvent(this, 'edit_result', 'Results', 'Click Supplement Name or Edit')
      if (item.params) {
        this.TOGGLE_DIALOG({ id: item.data.id, data: item.data, name: 'editResult', isOpen: true, title: `Edit supplement: ${item.data.supplementName}` })
      } else {
        this.TOGGLE_DIALOG({ id: item.id, data: item, name: 'editResult', isOpen: true, title: `Edit supplement: ${item.supplementName}` })
      }
    },
    async deleteResult (item) {
      await this.TOGGLE_DIALOG({
        id: item.data.id,
        name: 'confirm',
        isOpen: true,
        action: 'results/deleteResult',
        callback: (async () =>  {
          const result = await this.getResults({ id: this.$route.params.id, orderMethod: this.orderMethod.code })

          await this.$store.dispatch('visits/updateTotalSelectedResultSupplements', { totalSelectedResultSupplements: result.data.record.totalSelectedResultSupplements })
          this.getNotes(this.$route.params.id)
        }),
        description: 'Are you sure you want to delete the supplement?',
        title: 'Confirm deleting the Supplement'
      })
    },
    updateAnything() {
      this.getNotes(this.$route.params.id)
    },
    runAction (val) {
      val.params.action(val)
    },
    getProductInfo(supplement) {
      const products = supplement.resultProducts?.$values || []
      if (products.length === 0) return ''

      if (products.length === 1) {
        const p = products[0]
        return `${p.doses || ''} ${p.units || ''}`
      }

      const productsList = products
        .map(p => `${p.doses || ''} ${p.units || ''}`.trim())
        .join(', ')

      return productsList
    },
    handleMouseEnter(supplement) {
      this.hoveredSupplementId = supplement.id
      this.$emit('supplement-highlight', supplement.supplementId)
    },

    handleMouseLeave() {
      this.hoveredSupplementId = null
      this.$emit('supplement-unhighlight')
    },
  }
}
</script>

<style lang="scss" scoped>
.pane-content {
  position: relative;
}

.schedule-timeline {
  position: sticky;
  top: 0;
  display: grid;
  grid-template-columns: repeat(9, 0.5fr) minmax(auto, 1fr);
  align-items: stretch;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 0 15px;
  overflow-y: auto;
  scrollbar-width: none;
  z-index: 1;

  .time-slot {
    position: relative;
    min-width: 60px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    padding: 9px 0 7px;
    box-sizing: border-box;

    .dosage-total {
      width: 100%;
      height: 24px;
      font-size: 12px;
      font-weight: 600;
      line-height: 25px;
      color: #1A202C;
      background: rgba(208, 220, 255, 0.5);
      text-align: center;

    }

    &.time-slot--price {
      align-items: flex-end;
      justify-content: flex-end;
      padding: 4px 8px 7px 1px;

      &::before {
        content: none !important;
      }

      .time-label {
        font-size: 12px;
        font-weight: 500;
        line-height: 18px;
        color: #718096;
        text-transform: uppercase;
      }
    }

    &.time-slot--time:nth-last-child(2) {
      .dosage-total {
        border-top-right-radius: 2px;
        border-bottom-right-radius: 2px;
      }
    }

    &:first-child {
      .time-label {
        letter-spacing: -0.5px;
      }

      .dosage-total {
        border-top-left-radius: 2px;
        border-bottom-left-radius: 2px;
      }
    }

    &:not(:first-child) {
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 15px;
        background-color: #E4ECF7;
        height: 29px;
        width: 2px;
        border-radius: 2px;
      }
    }

    .time-icon {
      width: 24px;
      height: 24px;
    }

    .time-label {
      font-size: 10px;
      font-weight: 500;
      line-height: 18px;
      color: #1A202C;
      text-align: center;
      white-space: pre-line;
    }
  }
}

.supplement-schedule-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 5px;

  .supplement-row {
    border-radius: 8px;
    background-color: #ffffff;
    padding: 15px;
    border: 1px solid transparent;
    transition: all 0.3s ease;

    .supplement-info {
      display: grid;
      grid-template-columns: 1fr;
      row-gap: 8px;
    }

    &.is-hovered,
    &.is-expanded,
    &.is-highlighted {
      border-color: #008C8980;
      box-shadow: 0 0 10px 0 rgba(0, 140, 137, 0.3);
    }
  }

  .supplement-header {
    display: flex;
    align-items: center;
    column-gap: 10px;
    min-width: 0;
    cursor: pointer;

    .supplement-name {
      min-width: 0;
      display: flex;
      align-items: center;
      column-gap: 10px;
      font-size: 15px;
      font-weight: 600;
      line-height: 20px;
      color: #1A202C;

      &:hover {
        text-decoration: underline;
      }

      span {
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: pointer;

        &.disabled {
          pointer-events: none;
        }
      }
    }

    .supplement-actions {
      position: relative;
      height: 24px;
      width: 24px;

      ::v-deep {
        .v-btn {
          height: 24px;
          width: 24px;
        }
        .v-icon {
          color: #A0AEC0;
        }
      }

      .actions {
        &.actions--results {
          ::v-deep .v-menu {
            .v-menu__content {
              right: auto !important;
              left: 21px;
            }
          }
        }
      }
    }

    .total-price {
      font-size: 15px;
      font-weight: 600;
      line-height: 25px;
      color: #1A202C;
      margin-left: auto;
    }

    &.disabled {
      cursor: auto;
      pointer-events: none;
    }
  }

  .product-dosage {
    height: 36px;
    display: grid;
    grid-template-columns: repeat(9, 0.5fr) minmax(137px, 1fr);
    column-gap: 4px;
    align-items: stretch;

    &__inner {
      display: contents;

      .dosage-cell {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;

        &-content {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          font-weight: 500;
          line-height: 25px;
          color: #1A202C;
          background-color: #EBF2FA;
          border-radius: 8px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          transition: background-color .2s;

          &.active {
            background-color: #008C891A !important;
          }
        }
      }
    }
  }

  .products-details {
    display: flex;
    align-items: center;
    column-gap: 3px;
    background-color: rgba(0, 140, 137, 0.1);
    border-radius: 8px;
    padding: 5px 9px;
    min-width: 0;
    cursor: pointer;

    .product-summary {
      width: 100%;
      display: flex;
      align-items: center;
      column-gap: 14px;
      min-width: 0;

      .product-icon {
        display: flex;
        align-items: center;
        flex-shrink: 0;

        .product-count {
          font-size: 15px;
          font-weight: 500;
          line-height: 25px;
          color: #1A202C;
        }
      }

      .product-info {
        display: flex;
        align-items: center;
        min-width: 0;
      }

      .product-doses {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: #4A5568;
        min-width: 0;
      }
    }

    .expand-icon {
      flex-shrink: 0;
      transition: transform 0.2s ease;

      &.expanded {
        transform: rotate(180deg);
      }
    }
  }
}

.supplement-expanded {
  background-color: #ffffff;
  margin: 20px 10px 10px;
  overflow: hidden;

  .expanded-grid-header {
    display: grid;
    grid-template-columns: 48px repeat(5, 1fr);
    height: 50px;

    .grid-cell {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: 600;
      line-height: 12px;
      color: #8492A6;
      text-transform: uppercase;
      border: 0.5px solid #E2E8F0;
    }
  }

  .expanded-grid-body {
    .grid-row {
      display: grid;
      grid-template-columns: 48px repeat(5, 1fr);
      min-height: 50px;

      .grid-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;
        font-weight: 400;
        line-height: 20px;
        color: #1A202C;
        padding: 12px 10px;
        border: 0.5px solid #E2E8F0;

        .bottles-cell {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;

          .btn-control {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 26px;
            height: 26px;
            border-radius: 50%;
            background-color: #F7FAFC;
            cursor: pointer;

            &:disabled {
              opacity: 0.4;
              cursor: not-allowed;
            }
          }

          .bottle-count {
            font-weight: 500;
            font-size: 15px;
            line-height: 25px;
          }
        }
      }

      .checkbox-cell {
        display: flex;
        align-items: center;
        justify-content: center;

        .checkbox {
          ::v-deep .v-input {
            .v-input--selection-controls__input {
              margin: 0;
              width: 16px;
              height: 16px;
              border-radius: 16px;

              &:hover {
                .v-input--selection-controls__ripple {
                  &:before {
                    background-color: transparent;
                  }
                }
              }

              .v-icon {
                color: #C9CED6;
              }
            }
            .v-input--selection-controls__ripple {
              width: 16px;
              height: 16px;
              border-radius: 16px;
              left: 50%;
              top: 50%;
              transform: translate(-50%, -50%);
              margin: 0;
            }
          }
        }
      }
    }
  }
}
</style>
